import { useEffect, useState } from "react";
import { IoEye } from "react-icons/io5";
import { MdDelete, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import AddCategoryModal from "./component/AddCategoryModal";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import { useCategory } from "../useHook";
import Swal from "sweetalert2";
import Loader, { ImageWithFallback } from "../../../Utils/loader";
import {
  FiEdit2,
  FiFilter,
  FiPlus,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { usePermission, SystemPermissions } from "../../../Utils/permissionAccess";

function Categories() {
  const { hasPermission } = usePermission();
  const canRead = hasPermission(SystemPermissions.ShopCategories.READ);
  const canCreate = hasPermission(SystemPermissions.ShopCategories.CREATE);
  const canDelete = hasPermission(SystemPermissions.ShopCategories.DELETE);
  const canToggle = hasPermission(SystemPermissions.ShopCategories.TOGGLE);
  const canPosition = hasPermission(SystemPermissions.ShopCategories.POSITION);
  const showActions = canRead || canCreate || canDelete;

  const [modalOpen, setModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [categoryNameQuery, setCategoryNameQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedCategoryForPosition, setSelectedCategoryForPosition] =
    useState(null);

  const {
    addCategory,
    editCategory,
    getCategoriesWithPagination,
    updateCategoryStatus,
    searchCategories,
    deleteCategory,
    updateCategoryPosition,
  } = useCategory();
  const navigate = useNavigate();

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const fetchCategories = async (currentOffset = offset, showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      await getCategoriesWithPagination(
        setCategories,
        setTotal,
        currentOffset,
        limit,
        categoryNameQuery,
        statusFilter,
      );
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setIsLoading(true);
      setOffset(0);
      const response = await searchCategories(
        categoryNameQuery,
        statusFilter,
        0,
        limit,
      );
      setCategories(response?.categories || []);
      const totalRecords =
        response?.pagination?.totalRecords || response?.categories?.length || 0;
      setTotal(totalRecords);
    } catch (error) {
      console.error("Error in search:", error);
      Swal.fire({
        title: "Error",
        text: "There was an issue with the search.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setCategoryNameQuery("");
    setStatusFilter("");
    setOffset(0);
    fetchCategories(0);
  };

  const handlePositionClick = (category) => {
    setSelectedCategoryForPosition(category);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (categoryId, newPosition) => {
    await updateCategoryPosition(categoryId, newPosition);
    await fetchCategories(offset);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedCategoryForPosition(null);
  };

  useEffect(() => {
    fetchCategories();
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setIsLoading(true);
    setOffset(newOffset);
    fetchCategories(newOffset, false);
  };

  const handleSaveCategory = async (categoryData, id = null) => {
    setIsLoading(true);
    try {
      let response;

      if (id) {
        response = await editCategory(id, categoryData);
        if (response) {
          await fetchCategories(offset);
        }
      } else {
        response = await addCategory(categoryData);
        if (response) {
          setOffset(0);
          await fetchCategories(0);
        }
      }

      if (response) {
        toggleModal();
        Swal.fire("Success", "Category saved successfully!", "success");
      }
    } catch (error) {
      console.error("Error in handleSaveCategory:", error);
      Swal.fire("Error", error.message || "Failed to save category.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditCategory = (category) => {
    setCurrentCategory(category);
    toggleModal();
  };

  const handleToggleStatus = async (id, currentStatus) => {
    setIsLoading(true);
    try {
      const newStatus = !currentStatus;
      const response = await updateCategoryStatus(id, newStatus);

      if (response) {
        setCategories((prevCategories) =>
          prevCategories.map((category) =>
            category.id === id ? { ...category, status: newStatus } : category,
          ),
        );

        Swal.fire({
          title: "Success",
          text: `Category status has been ${
            newStatus ? "activated" : "deactivated"
          } successfully!`,
          icon: "success",
          timer: 1000,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to update category status.",
        icon: "error",
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPage = (id) => {
    navigate("/category/view", { state: { id } });
  };

  const handleDeleteCategory = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });
    if (result.isConfirmed) {
      setIsLoading(true);
      try {
        const response = await deleteCategory(id);
        if (response) {
          await fetchCategories(offset);
          Swal.fire("Success", "Category deleted successfully!", "success");
        }
      } catch (error) {
        console.error("Error in handleDeleteCategory:", error);
        Swal.fire(
          "Error",
          error.message || "Failed to delete category.",
          "error",
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (isLoading) {
    return <Loader message="Loading categories..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h2 className="text-2xl font-bold">Categories</h2>

        {canCreate && (
          <button
            onClick={() => {
              setCurrentCategory(null);
              toggleModal();
            }}
            className="btn btn-primary h-[44px]"
          >
            <FiPlus className="text-white" />
            Add Category
          </button>
        )}
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap xlCustom:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by category name"
            value={categoryNameQuery}
            onChange={(e) => setCategoryNameQuery(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>

        <select
          className="status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="" className="opacity-25">
            Select Status
          </option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>
      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Position</th>
                <th className="table-th">Image</th>
                <th className="table-th">Category Name</th>
                <th className="table-th">Product Count</th>
                <th className="table-th">Status</th>
                {showActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {categories?.length === 0 ? (
                <tr>
                  <td
                    colSpan={showActions ? 6 : 5}
                    className="text-center py-10 text-gray-500 text-lg font-medium"
                  >
                    No data found
                  </td>
                </tr>
              ) : (
                categories?.map((category, index) => (
                  <tr
                    key={category.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      {canPosition ? (
                        <button
                          type="button"
                          onClick={() => handlePositionClick(category)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        >
                          {category.position ?? offset + index + 1}
                        </button>
                      ) : (
                        <span className="font-medium text-gray-500">
                          {category.position ?? offset + index + 1}
                        </span>
                      )}
                    </td>
                    <td className="table-td">
                      <ImageWithFallback
                        src={category.image}
                        alt={category.name}
                        className="w-16 h-16 object-cover rounded-full shadow-lg"
                      />
                    </td>
                    <td className="table-td">{category.name}</td>
                    <td className="table-td">{category.productCount}</td>
                    <td className="table-td">
                      {canToggle ? (
                        <div
                          className={`status-toggle ${category.status ? "active" : ""}`}
                          onClick={() =>
                            handleToggleStatus(category.id, category.status)
                          }
                        >
                          <div className="status-toggle-handle"></div>
                        </div>
                      ) : (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          category.status ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                        }`}>
                          {category.status ? "Active" : "Inactive"}
                        </span>
                      )}
                    </td>

                    {showActions && (
                      <td className="table-td flex space-x-2 mt-3">
                        {canRead && (
                          <button
                            className="btn btn-view"
                            onClick={() => handleViewPage(category.id)}
                          >
                            <IoEye className="text-white" /> View
                          </button>
                        )}
                        {canCreate && (
                          <button
                            className="btn btn-edit text-white"
                            onClick={() => handleEditCategory(category)}
                          >
                            <FiEdit2 className="text-white" /> Edit
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn btn-delete text-white"
                            onClick={() => handleDeleteCategory(category.id)}
                          >
                            <MdDelete className="text-white" /> Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {total > limit && (
        <div className="mt-6">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      <AddCategoryModal
        modalOpen={modalOpen}
        toggleModal={toggleModal}
        category={currentCategory}
        handleSaveCategory={handleSaveCategory}
      />

      {isPositionModalOpen && selectedCategoryForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={
            selectedCategoryForPosition.position ??
            offset +
              categories.findIndex(
                (c) => c.id === selectedCategoryForPosition.id,
              ) +
              1
          }
          recordId={selectedCategoryForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Category"
        />
      )}
    </div>
  );
}

export default Categories;
